
export * from './enums.ts'
export * from './network.ts'
export * from './version.ts'
export * from './game.ts'
export * from './notifications.ts'
export {type ContinentCode, type CountryCode, type RegionCode, CONTINENT_CODES} from './region.ts'

export {SharedStrings} from './SharedStrings.ts'

export type {ImageURL, SoundURL} from './assets.ts'
export type {Brand} from './brand.ts'

export enum AccessLevel {
    Administrator = 0, // Full access to development and server commands
    User = 1, // Normal user
    // Guest = 2, // !Warning Old access level that was removed and should not be used again
    // Customer = 3, // !Warning Old access level that was removed and should not be used again
    Manager = 4, // Access to managerial commands
    Moderator = 5, // Access to moderation commands
    // Player = 6, // !Warning Old access level that was removed and should not be used again
}

// represents a BIGINT id in the database, which is returned as a string from TypeORM due to a BIGINT possibly being larger than the Node number range
export type BigId = string

// https://discord.com/developers/docs/reference#snowflakes
// represents a unique BIGINT id in the database for Discord users and servers. These are external ids, generated by (and uniqueness enforced by) Discord
export type DiscordSnowflake = string

export type UserSessionId = `${ServerId}.${string}`

// https://stackoverflow.com/a/55479659/14514368
// Create a type excluding all instance methods of a class T
export type NonFunctionProperties<T> = Pick<T, NonFunctionPropertyNames<T>>
type NonFunctionPropertyNames<T> = {
    [K in keyof T]: T[K] extends (...args: any) => void ? never : K;
}[keyof T]

export type Mutable<T> = {
    -readonly [K in keyof T]: T[K];
}

export const CHAT_MAXIMUM_CHARACTER_LIMIT = 200

export type ConstructorOf<C> = new (...args: any[]) => C

export enum DeviceType {
    Unknown = 0,
    Web = 1,
    Discord = 2,
    AppleWeb = 3,
    AppleApp = 4,
    AndroidWeb = 5,
    AndroidApp = 6,
    MobileUnknown = 7,
    DiscordMobile = 8,
}

export const MOBILE_APP_DEVICE_TYPES = [
    DeviceType.AppleApp,
    DeviceType.AndroidApp,
]

export const MOBILE_WEB_DEVICE_TYPES = [
    DeviceType.AppleWeb,
    DeviceType.AndroidWeb,
]

export const MOBILE_UNKNOWN_DEVICE_TYPES = [
    DeviceType.MobileUnknown,
]

export const MOBILE_DEVICE_TYPES = [
    ...MOBILE_APP_DEVICE_TYPES,
    ...MOBILE_WEB_DEVICE_TYPES,
    ...MOBILE_UNKNOWN_DEVICE_TYPES,
    DeviceType.DiscordMobile,
]

export const DISCORD_DEVICE_TYPES = [
    DeviceType.Discord,
    DeviceType.DiscordMobile,
]

export enum OnlineCountCategory {
    GamePlayer = 1,
    GameSpectator = 2,
    RoomPlayer = 3,
    LobbyUser = 4,
    Socket = 5,
}

export interface OnlineCountRecord {
    category: OnlineCountCategory
    device: DeviceType
    count: number
}

export enum ServerState {
    Starting = 1,
    Running = 2,
    Draining = 3,
    Stopping = 4
}

export type ServerId = string

// minlength 2, maxlength 20, only a-z
export type ServerType =
    'socket' | // socket router
    'lobby' |
    'game'

export type ServerRecipient = ServerId | ServerType

export type OrmRelation<T> = T

export type TimeoutId = ReturnType<typeof setTimeout> | ReturnType<typeof setInterval> | number

export type FilterKeysByPrefix<T, TPrefix extends string> = {
    [K in keyof T as K extends `${TPrefix}${string}` ? K : never]: T[K];
}

export type RemoveKeyPrefix<T, TPrefix extends string> = {
    [K in keyof T as K extends `${TPrefix}${infer Rest}` ? Rest : K]: T[K];
}

export class AbortError extends Error {
    constructor() {
        super('AbortError')
    }
}

export const COLONIST_SYSTEM_USER_ID: BigId = '9223372036854775807'

export const REDIRECT_NEW_ACCOUNTS_TO_MOBILE_WEB_COLONIST_VERSION: number = 281 * 10

export const FORCE_PORTRAIT_INACTIVITY_THRESHOLD_VERSION_DURATION: number = 4 // ~28 days age
